import { useState, useRef, useEffect, useMemo } from "react";
import Button from "../../ui/Button";
import { useGetLeads, useDeleteLead, useUpdateLead, useUpdateComment, useTotalStaff, useUser, useGetPermissions, useDeleteComment } from "../../Use-auth";
import CreateLead from "./CreateLead";
import AddCommentInput from "./AddCommentInput";
import { MoveLeft, Trash2, Pencil, CalendarRange, MessageCircle, User, X, HandCoins, Eye, Link } from "lucide-react";
import DateRangePicker from "../../ui/DateRangePicker";
import { format } from "date-fns";
import Pagination from "../../ui/Pagination";
import { useLocation } from "wouter";
import SkeletonPage from "../../ui/SkeletonPage ";
import PermissionPopup from "../../ui/PermissionPopup";
import PreviewLead from "./PreviewLeads";
import ProjectInformation from "./ProjectInformation";
import { api } from "../../../lib/api";

const formatPhone = (phone) => {
    if (!phone) return "-";
    const cleaned = phone.replace(/\D/g, "");

    if (cleaned.length === 12 && cleaned.startsWith("91")) {
        return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length > 10) {
        return `+${cleaned}`;
    }
    return phone;
};

export default function LeadsTable({ leadsType, setActiveTab }) {
    const { data, isLoading, refetch } = useGetLeads();
    const deleteLead = useDeleteLead();
    const updateLead = useUpdateLead();
    const updateLeadComment = useUpdateComment();
    const deleteComment = useDeleteComment();
    const { data: user } = useUser();
    const { data: employees } = useTotalStaff();
    const { data: existingPermissions } = useGetPermissions();
    const managerCurremt = user.department === 'Management';
    const isManagerorAdmin = user?.department === "Admin" || user?.department === "Management";
    const statusRef = useRef(null);
    const [createLead, setCreateLead] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [dateRange, setDateRange] = useState({ startDate: new Date(), endDate: new Date() });
    const [isDateApplied, setIsDateApplied] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showStatus, setShowStatus] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [showAssignBox, setShowAssignBox] = useState(null);
    const [assignLoading, setAssignLoading] = useState({});
    const [showPopup, setShowPopup] = useState(null);
    const [tempStatus, setTempStatus] = useState({});
    const [, setLocation] = useLocation();
    const assignBoxRef = useRef(null);
    const formsPerPage = 20;
    const [editingComments, setEditingComments] = useState({});
    const [tabLeads, setTabLeads] = useState("client");
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedOwner, setSelectedOwner] = useState("");
    const [deptSearch, setDeptSearch] = useState("");

    const getStatusOptions = (lead) => {
        const allStatusesNormal = ["Proposal Sent", "Qualified", "Won", "Lost"];

        if (leadsType === "normalleads") {
            const currentStatus = lead.leadStatus;
            switch (currentStatus) {
                case "Proposal Sent":
                    return [...allStatusesNormal.filter(s => s !== "Proposal Sent"), currentStatus];
                case "Qualified":
                    return [...allStatusesNormal.filter(s => !["Proposal Sent", "Qualified"].includes(s)), currentStatus];
                case "Won":
                    return ["Lost", currentStatus];
                case "Lost":
                    return ["Won", currentStatus];
                default:
                    return allStatusesNormal;
            }
        }

        if (leadsType === "leads") {
            return ["New", "Contacted", "Proposal Sent", "Qualified"];
        }

        if (leadsType === "wonleads") {
            const currentStatus = lead.leadStatus;
            switch (currentStatus) {
                case "Won":
                    return ["Lost", currentStatus];
                case "Lost":
                    return [currentStatus];
                default:
                    return ["Won", "Lost"];
            }
        }

        return [];
    };

    const getStatusClasses = (status) => ({
        New: "bg-green-100 text-green-800",
        Contacted: "bg-yellow-100 text-yellow-800",
        Qualified: "bg-blue-100 text-blue-800",
        "Proposal Sent": "bg-purple-100 text-purple-800",
        Won: "bg-emerald-100 text-emerald-800",
        Lost: "bg-red-100 text-red-800",
        normallost: "bg-red-100 text-red-800",
        wonlost: "bg-red-100 text-red-800",
    }[status] || "bg-gray-100 text-gray-800");

    useEffect(() => {
        const handleClickOutsideStatus = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setShowStatus(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutsideStatus);
        return () => {
            document.removeEventListener("mousedown", handleClickOutsideStatus);
        };
    }, []);

    const employee = employees
        ?.filter(emp =>
            emp.departmentName === "Sales" ||
            emp.departmentName === "Admin"
        )
        ?.map(emp => ({
            userId: emp.employeeId,
            email: emp.officeEmail,
            userName: `${emp.firstName} ${emp.lastName || ""}`,
        })) || [];

    const managerInfo = employees
        ?.filter(emp =>
            emp.departmentName === "Management" ||
            emp.departmentName === "Admin"
        )
        ?.map(emp => ({
            userId: emp.employeeId,
            email: emp.officeEmail,
            userName: `${emp.firstName} ${emp.lastName || ""}`,
        })) || [];

    const allLeads = data?.data
        ?.filter((lead) => {
            if (isManagerorAdmin) return true;
            return lead.userId === user.userId;
        })
        ?.filter((lead) => {
            if (!selectedOwner) return true;
            return lead.userId === selectedOwner;
        })
        .filter((lead) => {
            if (!deptSearch) return true;

            const search = deptSearch.toLowerCase();

            const fullName = `${lead.firstName || ""} ${lead.lastName || ""}`.toLowerCase();
            const email = (lead.email || "").toLowerCase();

            return fullName.includes(search) || email.includes(search);
        })
        ?.filter((lead) => {
            if (leadsType === "leads") {
                return ["New", "Contacted"].includes(lead.leadStatus);
            }
            if (leadsType === "normalleads") {
                return ["Proposal Sent", "Qualified", "normallost"].includes(lead.leadStatus);
            }
            if (leadsType === "wonleads") {
                return ["Won", "Lost", "wonlost"].includes(lead.leadStatus);
            }
            return true;
        })
        ?.map((lead) => ({
            ...lead,
            parentUserId: lead.userId,
        }))
        .filter(lead => {
            if (!isDateApplied) return true;

            const created = new Date(lead.createdAt);
            created.setHours(0, 0, 0, 0);

            const start = new Date(dateRange.startDate);
            start.setHours(0, 0, 0, 0);

            const end = new Date(dateRange.endDate);
            end.setHours(0, 0, 0, 0);

            return created >= start && created <= end;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];

    const totalPages = Math.ceil(allLeads.length / formsPerPage);
    const currentForms = allLeads.slice((currentPage - 1) * formsPerPage, currentPage * formsPerPage);

    const handlePageChange = (page) => setCurrentPage(page);

    // Delete lead
    const handleDelete = (lead) => {
        if (!window.confirm("Delete this lead?")) return;
        deleteLead.mutate({ userId: lead.parentUserId, leadId: lead._id });
    };

    // Edit lead
    const handleEdit = (lead) => {
        setEditingLead(lead);
        setCreateLead(true);

        setIsEditMode(true);
        setTabLeads("client");
    };

    // Toggle status dropdown
    const handleShowStatus = (id) => setShowStatus(prev => (prev === id ? null : id));

    const handleStatusChange = (lead, newStatus) => {
        if (newStatus !== "Contacted") {
            if (!lead.assignEmployes || lead.assignEmployes.length === 0) {
                alert("Please assign at least one user before changing the status.");
                setTempStatus('');
                return;
            }
        }

        setUpdatingStatusId(lead._id);
        updateLead.mutate(
            {
                userId: lead.parentUserId, leadId: lead._id,
                leadStatus: newStatus, leadsType: leadsType, sendManger: lead.sendManger || null
            },
            {
                onSuccess: () => {
                    setShowStatus(null);
                    setUpdatingStatusId(null);
                    setTempStatus(prev => ({ ...prev, [lead._id]: undefined }));
                    refetch();
                    redirectByStatus(newStatus);
                },
                onError: (err) => {
                    console.error("Update Error:", err);
                    setUpdatingStatusId(null);
                }
            }
        );
    };

    // Assign/Unassign users
    const handleAssignUser = (lead, emp) => {
        const key = `${lead._id}_${emp.userId}`;
        setAssignLoading(prev => ({ ...prev, [key]: true }));

        const currentAssigns = lead.assignEmployes || [];
        const newAssigns = currentAssigns.some(a => a.userId === emp.userId)
            ? currentAssigns
            : [...currentAssigns, { userId: emp.userId, email: emp.email, userName: emp.userName }];

        updateLead.mutate(
            { userId: lead.parentUserId, leadId: lead._id, assignEmployes: newAssigns },
            {
                onSuccess: () => {
                    setAssignLoading(prev => ({ ...prev, [key]: false }));
                    setShowAssignBox(null);
                    refetch();
                },
                onError: (err) => {
                    console.error("Assign Error:", err);
                    setAssignLoading(prev => ({ ...prev, [key]: false }));
                }
            }
        );
    };

    const handleUnassignUser = (lead, emp) => {
        const key = `${lead._id}_${emp.userId}`;
        setAssignLoading(prev => ({ ...prev, [key]: true }));

        const newAssigns = (lead.assignEmployes || []).filter(a => a.userId !== emp.userId);

        updateLead.mutate(
            { userId: lead.parentUserId, leadId: lead._id, assignEmployes: newAssigns },
            {
                onSuccess: () => {
                    setAssignLoading(prev => ({ ...prev, [key]: false }));
                    setShowAssignBox(null);
                    refetch();
                },
                onError: (err) => {
                    console.error("Unassign Error:", err);
                    setAssignLoading(prev => ({ ...prev, [key]: false }));
                }
            }
        );
    };

    const isAdminOrManager = user.department === "Admin" || user.department === "Management";

    const redirectByStatus = (status) => {
        // 🔥 normalize status (important)
        const isLost =
            status === "Lost" ||
            status === "normallost" ||
            status === "wonlost";

        if (isAdminOrManager) {
            if (leadsType === "normalleads" && isLost) {
                setActiveTab("normalleads");
                return;
            }

            if (["New", "Contacted"].includes(status)) {
                setActiveTab("leads");
            } else if (
                ["Proposal Sent", "Qualified", "normallost"].includes(status)
            ) {
                setActiveTab("normalleads");
            } else if (
                ["Won", "Lost", "wonlost"].includes(status)
            ) {
                setActiveTab("wonleads");
            }

            return;
        }

        if (leadsType === "normalleads" && isLost) {
            return;
        }

        switch (status) {
            case "New":
            case "Contacted":
                setLocation("/lead-contacts");
                break;

            case "Proposal Sent":
            case "Qualified":
            case "normallost":
                setLocation("/lead");
                break;

            case "Won":
            case "Lost":
            case "wonlost":
                setLocation("/active-contacts");
                break;
            default:
                break;
        }
    };

    // Close assign box when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assignBoxRef.current && !assignBoxRef.current.contains(event.target)) {
                setShowAssignBox(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isAdmin = user?.department === "Admin";

    const currentUserPermissions = isAdmin
        ? {
            employees: {
                sales_view: true,
            },
            management: {
                sales_view: true,
            },
        }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome = useMemo(() => {
        if (!user || !existingPermissions) return false;
        return isAdmin || currentUserPermissions?.employees?.sales_view || currentUserPermissions?.management?.sales_view;
    }, [user, existingPermissions, isAdmin, currentUserPermissions]);

    const totalWonAmount = allLeads
        .filter(lead => lead.leadStatus === "Won")
        .reduce((sum, lead) => {
            const projects = lead.projects || [];

            const leadTotal = projects.reduce((pSum, project) => {
                return pSum + (Number(project.budget) || 0);
            }, 0);

            return sum + leadTotal;
        }, 0);

    const totalLostAmount = allLeads
        .filter(lead => lead.leadStatus === "Lost")
        .reduce((sum, lead) => {
            const projects = lead.projects || [];

            const leadTotal = projects.reduce((pSum, project) => {
                return pSum + (Number(project.budget) || 0);
            }, 0);

            return sum + leadTotal;
        }, 0);

    if (isLoading || !user || existingPermissions === undefined) {
        return (
            <div className="p-6">
                <SkeletonPage />
            </div>
        );
    }

    if (!canViewHome) {
        return (
            <div className="relative h-[90.7vh] overflow-hidden">
                <PermissionPopup />
            </div>
        );
    }

    const handleDeleteComment = (commentId) => {
        if (!window.confirm("Delete this comment?")) return;

        deleteComment.mutate(
            { commentId },
            {
                onSuccess: () => {
                    const updatedComments = showPopup.comments.filter(
                        (c) => c._id !== commentId
                    );

                    setShowPopup((prev) => ({
                        ...prev,
                        comments: updatedComments,
                    }));
                },
            }
        );
    };

    return (
        <div className="relative p-2 rs:p-6">
            {!createLead ? (
                <div>
                    {/* Top controls */}
                    <div className="flex flex-col zs:flex-row justify-end gap-2 mb-5">
                        {isManagerorAdmin && (
                            <div>
                                <select
                                    value={selectedOwner}
                                    onChange={(e) => setSelectedOwner(e.target.value)}
                                    className="input text-sm flex-1 min-w-[120px] sm:max-w-[200px] px-3 py-2"
                                >
                                    <option value="">All Owners</option>

                                    {employee?.map((emp) => (
                                        <option key={emp.userId} value={emp.userId}>
                                            {emp.userName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="w-full sm:w-64">
                            <input
                                type="text"
                                value={deptSearch}
                                onChange={(e) => setDeptSearch(e.target.value)}
                                placeholder="Search"
                                className="input w-full"
                            />
                        </div>
                        <div>
                            <div
                                className="cursor-pointer border p-1 md:p-[10px] rounded-md text-sm md:text-base bg-white flex gap-2"
                                onClick={() => setShowDatePicker(true)}
                            >
                                <CalendarRange className="w-4 h-4 md:w-5 md:h-5" />
                                <span className="text-[10px] md:text-sm">
                                    {format(dateRange.startDate, "dd-MM-yyyy")} → {format(dateRange.endDate, "dd-MM-yyyy")}
                                </span>
                            </div>

                            <DateRangePicker
                                open={showDatePicker}
                                selectedRange={dateRange}
                                onApply={(range) => {
                                    if (range.startDate && range.endDate) {
                                        setDateRange(range);
                                        setIsDateApplied(true);
                                    }
                                }}
                                onClose={() => setShowDatePicker(false)}
                            />
                        </div>

                        {!managerCurremt && leadsType !== "normalleads" && leadsType !== "wonleads" && (<Button
                            onClick={() => { setEditingLead(null); setCreateLead(true); setIsEditMode(false); }}
                        >
                            + Create Lead
                        </Button>)}
                    </div>

                    {/* Table Header */}
                    <h2 className="text-[14px] sm:text-[16px] md:text-[20px] font-medium mb-4">
                        {leadsType === "leads" && "Contacts"}
                        {leadsType === "normalleads" && "Leads Contacts"}
                        {leadsType === "wonleads" && "Active Contacts"}
                    </h2>

                    <table className="min-w-full border-collapse">
                        <thead>
                            <tr className="bg-white text-xs font-semibold text-gray-600 border-b">
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xs:table-cell">Applied Date</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm ">Client Name</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left hidden xl:table-cell text-[11px] sm:text-sm">Email</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left hidden xl:table-cell text-[11px] sm:text-sm">Phone</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left hidden xl:table-cell text-[11px] sm:text-sm">Source</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left  text-[11px] sm:text-sm"> Id</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left  text-[11px] sm:text-sm hidden custom:table-cell">Spent</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden zx:table-cell">Budget</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xs:table-cell">Type</th>
                                {isManagerorAdmin && (
                                    <th className="hidden custom:table-cell px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm">
                                        Owner
                                    </th>
                                )}
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden zs:table-cell">Status</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden zs:table-cell">Assigned</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden lg:table-cell">Purposal Link</th>
                                <th className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={12} className="py-6">
                                        <SkeletonPage />
                                    </td>
                                </tr>
                            ) : currentForms.length > 0 ? (
                                currentForms.map((lead) => {

                                    const totalBudget = (lead.projects || []).reduce(
                                        (sum, project) => sum + (Number(project.budget) || 0),
                                        0
                                    );

                                    return (
                                        <tr
                                            key={lead._id}
                                            className="text-sm border-b bg-white hover:bg-[#f3f4f6] transition-colors duration-200"
                                        >
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xs:table-cell ">{new Date(lead.createdAt).toLocaleDateString()}</td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] truncate max-w-[120px] sm:text-sm font-medium">{lead.firstName} {lead.lastName}</td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] truncate max-w-[120px] sm:text-sm text-blue-600 hidden xl:table-cell">{lead.email || '-'}</td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xl:table-cell truncate max-w-[120px]">{formatPhone(lead.phone)}</td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xl:table-cell truncate max-w-[100px]" >
                                                <span className="px-2 py-1 rounded text-xs truncate max-w-[120px]">{lead.projects?.[0]?.leadSource || '-'}</span></td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm ">
                                                {lead.projects?.[0]?.leadId || '-'}
                                            </td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden custom:table-cell ">
                                                {lead.projects?.[0]?.spentConnects || '0'}
                                            </td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden zx:table-cell">${totalBudget || '0.00'}</td>
                                            <td className="flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden xs:table-cell">{lead.projects?.[0]?.rate || '-'}</td>
                                            {isManagerorAdmin && (<td className="hidden custom:table-cell flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm">{lead.leadOwner || '-'}</td>)}

                                            {/* Status */}
                                            <td className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm hidden zs:table-cell">
                                                <div className={`inline-flex items-left gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusClasses(lead.leadStatus)}`}>
                                                    {updatingStatusId === lead._id && <span className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-1"></span>}
                                                    {lead.leadStatus === "normallost" || lead.leadStatus === "wonlost"
                                                        ? "Lost"
                                                        : lead.leadStatus}
                                                    {!managerCurremt && (<>
                                                        <Pencil size={14} color="orange" className="cursor-pointer" onClick={() => handleShowStatus(lead._id)} />
                                                        {showStatus === lead._id && (
                                                            <div ref={statusRef}>
                                                                <select
                                                                    className="ml-2 border rounded px-1 text-xs"
                                                                    value={tempStatus[lead._id] ?? lead.leadStatus}
                                                                    onChange={(e) => {
                                                                        const newStatus = e.target.value;
                                                                        setTempStatus(prev => ({ ...prev, [lead._id]: newStatus }));
                                                                        handleStatusChange(lead, newStatus);
                                                                    }}
                                                                >
                                                                    {getStatusOptions(lead).map(opt => (
                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </>)}

                                                </div>
                                            </td >

                                            <td className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm relative hidden zs:table-cell">
                                                <div
                                                    onClick={() => setShowAssignBox(showAssignBox === lead._id ? null : lead._id)}
                                                    className="inline-flex gap-1 cursor-pointer"
                                                >
                                                    {lead.assignEmployes?.length > 0 ? lead.assignEmployes.map(emp => (
                                                        <div key={emp.userId} className="w-8 h-8 rounded-full bg-[#1f2937] text-white flex items-center justify-center font-semibold text-[11px]">
                                                            {emp.userName.split(" ").map(w => w[0]).join("").toUpperCase()}
                                                        </div>
                                                    )) : <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center"><User size={14} /></div>}
                                                </div>

                                                {!managerCurremt && showAssignBox === lead._id && (
                                                    <div ref={assignBoxRef} className="absolute top-10 left-0 w-60 bg-white border shadow-md rounded-md z-50">
                                                        {managerInfo.map(emp => {
                                                            const isAssigned = lead.assignEmployes?.some(a => a.userId === emp.userId);
                                                            const key = `${lead._id}_${emp.userId}`;
                                                            return (
                                                                <div key={emp.userId}
                                                                    className="group border-b flex items-center gap-3 px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100"
                                                                    onClick={() => {
                                                                        if (!assignLoading[key]) {
                                                                            isAssigned ? handleUnassignUser(lead, emp) : handleAssignUser(lead, emp);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className={`w-7 h-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold ${isAssigned ? "bg-red-400" : "bg-gray-400"}`}>
                                                                        {emp.userName.split(" ").map(w => w[0]).join("").toUpperCase()}
                                                                    </div>
                                                                    <span className="truncate flex items-center gap-1">
                                                                        <div className="text-[12px]">{emp.userName}</div>
                                                                        {assignLoading[key] && <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>}
                                                                        {isAssigned && !assignLoading[key] && <X size={16} color="red" className=" transition-opacity ml-[20px]" />}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td >
                                            <td className="hidden lg:table-cell flex-1 px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm ">
                                                {lead.projects?.[0]?.jobLink ? (
                                                    <a href={lead.projects[0].jobLink} target="_blank" className="text-blue-600 underline">
                                                        <Link size={16} />
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            {/* Actions */}
                                            <td className="px-2 sm:px-2 py-1 sm:py-2 text-left text-[11px] sm:text-sm relative">
                                                <div className="flex gap-1 items-center">
                                                    {(user.department === "Management"
                                                        ? lead.assignEmployes?.some(emp => emp.userId === user.userId)
                                                        : true
                                                    ) && (
                                                            <div
                                                                className="p-1 rounded cursor-pointer hover:bg-blue-100"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setShowPopup(lead);
                                                                }}
                                                            >
                                                                <MessageCircle size={14} color="blue" />
                                                            </div>
                                                        )}

                                                    <div className="p-1 rounded cursor-pointer hover:bg-orange-100" onClick={() => handleEdit(lead)}>
                                                        <Pencil size={14} color="orange" /></div>
                                                    {!managerCurremt && (<div className="p-1 rounded cursor-pointer hover:bg-red-100" onClick={() => handleDelete(lead)}>
                                                        <Trash2 size={14} color="red" /></div>)}
                                                </div>
                                            </td >
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan={12} className="text-center py-4 text-sm text-gray-400">
                                        No leads found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {/* Pagination */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />
                </div>
            ) : (
                <div>
                    <div>
                        {isEditMode && (
                            <div className='tabs-header flex  border-gray-300 mb-4  w-full bg-white '>
                                <div className='tabs-header flex border-b text-sm border-gray-300 mb-4 w-full bg-white'>
                                    <button
                                        onClick={() => setTabLeads("client")}
                                        className={`px-1 py-1 zx:px-4 zx:py-2 font-medium border-b-2 transition text-[12px] sm:text-sm ${tabLeads === "client"
                                            ? "border-blue-500 text-blue-500"
                                            : "border-transparent text-gray-500 hover:text-blue-500"
                                            }`}
                                    >
                                        <div className="flex gap-1 items-center">
                                            Client Information
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setTabLeads("project")}
                                        className={`px-1 py-1 zx:px-4 zx:py-2 font-medium border-b-2 transition text-[12px] sm:text-sm ${tabLeads === "project"
                                            ? "border-blue-500 text-blue-500"
                                            : "border-transparent text-gray-500 hover:text-blue-500"
                                            }`}
                                    >
                                        <div className="flex gap-1 items-center">
                                            Project Information
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setTabLeads("edit")}
                                        className={`px-1 py-1 zx:px-4 zx:py-2 font-medium border-b-2 transition text-[12px] sm:text-sm ${tabLeads === "edit"
                                            ? "border-blue-500 text-blue-500"
                                            : "border-transparent text-gray-500 hover:text-blue-500"
                                            }`}
                                    >
                                        <div className="flex gap-1 items-center">
                                            Edit Details
                                        </div>
                                    </button>
                                </div>
                            </div>)}
                    </div>
                    <div onClick={() => {
                        setCreateLead(false);
                        setIsEditMode(false);
                        setTabLeads("client");
                        setEditingLead(null);
                    }} className="inline-flex gap-2 items-center text-[14px] cursor-pointer back-btn">
                        <MoveLeft size={18} /> <span>Back</span></div>
                    {createLead && (
                        (!isEditMode || tabLeads === "edit") && (
                            <CreateLead
                                leadsType={leadsType}
                                setActiveTab={setActiveTab}
                                editingLead={editingLead}
                                setEditingLead={setEditingLead}
                                onDone={() => {
                                    setCreateLead(false);
                                    setIsEditMode(false);
                                    setTabLeads("client");
                                }}
                                isEditMode={isEditMode}
                            />
                        )
                    )}

                    {tabLeads === "client" && (
                        <PreviewLead
                            editingLead={editingLead}
                            formatPhone={formatPhone}
                        />)}
                    {tabLeads === "project" && (
                        <ProjectInformation
                            editingLead={editingLead}
                            setEditingLead={setEditingLead}
                            Trash2={Trash2}
                            Pencil={Pencil}
                            Eye={Eye}
                            Button={Button}
                            X={X}
                        />
                    )}
                </div>
            )}

            {/* Overlay */}
            <div
                className={`fixed inset-0 bg-black/40 z-[999999] transition-opacity duration-300 ${showPopup ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={() => setShowPopup(null)}
            />

            <div
                className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[99999999] shadow-2xl border-l
  transform transition-transform duration-300 ease-in-out
  ${showPopup ? "translate-x-0" : "translate-x-full"}`}
                onClick={(e) => e.stopPropagation()}
            >
                {showPopup && (
                    <div className="h-full flex flex-col relative">
                        <button
                            onClick={() => setShowPopup(null)}
                            className="absolute top-1 right-1 text-gray-500 hover:text-gray-800 z-50 bg-white rounded-full p-1 shadow"
                        >
                            <X size={18} />
                        </button>

                        <div className="p-2 sm:p-5 border-b bg-gray-50  z-10 pr-10">
                            <h2 className="text-[13px] sm:text-lg font-semibold text-gray-800 truncate max-w-[300px]">
                                {showPopup.projects?.[0]?.title || "No Title"}
                            </h2>

                            <p className="mt-1 text-[10px] sm:text-sm text-gray-600 line-clamp-3">
                                {showPopup.projects?.[0]?.descrition || "No description"}
                            </p>
                        </div>

                        <div className="flex-1 overflow-y-auto p-2 sm:p-5 space-y-4">

                            <h3 className="text-sm font-semibold text-gray-700">
                                Comments
                            </h3>
                            <ul className="space-y-3">
                                {showPopup.comments?.map((c, i) => {

                                    const isEditing = editingComments[i]?.isEditing || false;
                                    const editedComment =
                                        editingComments[i]?.text ?? c.comment;

                                    const handleSave = () => {
                                        if (!c._id) {
                                            console.error(" comment without ID", c);
                                            return;
                                        }

                                        updateLeadComment.mutate(
                                            {
                                                commentId: c._id,
                                                comment: editedComment,
                                            },
                                            {
                                                onSuccess: () => {
                                                    const updatedComments = showPopup.comments.map((com) =>
                                                        com._id === c._id
                                                            ? { ...com, comment: editedComment }
                                                            : com
                                                    );

                                                    setShowPopup((prev) => ({
                                                        ...prev,
                                                        comments: updatedComments,
                                                    }));

                                                    setEditingComments((prev) => ({
                                                        ...prev,
                                                        [i]: { ...prev[i], isEditing: false },
                                                    }));
                                                },
                                            }
                                        );
                                    };

                                    return (

                                        <li
                                            key={i}
                                            className="bg-gray-50 border rounded-lg p-1 sm:p-2 shadow-sm"
                                        >

                                            {isEditing ? (

                                                <div className="space-y-2">

                                                    <textarea
                                                        value={editedComment}
                                                        onChange={(e) =>
                                                            setEditingComments((prev) => ({
                                                                ...prev,
                                                                [i]: {
                                                                    ...prev[i],
                                                                    text: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                        className="w-full border rounded-md p-2 text-[10px] sm:text-[12px] resize-none focus:ring-1 focus:ring-blue-400"
                                                        rows={3}
                                                        placeholder="Edit your comment..."
                                                    />


                                                    <div className="flex justify-end gap-2">

                                                        <button
                                                            onClick={handleSave}
                                                            className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                                        >
                                                            Save
                                                        </button>

                                                        <button
                                                            onClick={() =>
                                                                setEditingComments((prev) => ({
                                                                    ...prev,
                                                                    [i]: {
                                                                        ...prev[i],
                                                                        isEditing: false,
                                                                    },
                                                                }))
                                                            }
                                                            className="px-3 py-1 bg-gray-200 rounded text-xs hover:bg-gray-300"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>

                                                </div>

                                            ) : (

                                                <>
                                                    <p className="text-[10px] sm:text-[12px] text-gray-800 leading-relaxed break-words whitespace-pre-wrap">
                                                        {c.comment}
                                                    </p>

                                                    <div className="mt-1 flex justify-between items-center text-xs">

                                                        <div className="text-blue-600 font-medium text-[8px] sm:text-[10px]">
                                                            {c.userName} ({c.userId})
                                                        </div>

                                                        <div className="text-gray-500 text-[8px] sm:text-[10px]">
                                                            {new Date(c.createdAt).toLocaleString()}
                                                        </div>
                                                    </div>

                                                    <div className="flex gap-2 items-center">
                                                        <button
                                                            onClick={() => handleDeleteComment(c._id)}
                                                            className="text-red-500 text-[8px] sm:text-[10px] hover:underline"
                                                        >
                                                            Delete
                                                        </button>

                                                        <button
                                                            disabled={!c._id}
                                                            onClick={() =>
                                                                setEditingComments((prev) => ({
                                                                    ...prev,
                                                                    [i]: {
                                                                        text: c.comment,
                                                                        isEditing: true,
                                                                    },
                                                                }))
                                                            }
                                                            className={` text-[8px] sm:text-[10px] ${c._id ? "text-orange-500 hover:underline" : "text-gray-400 cursor-not-allowed"
                                                                }`}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </li>
                                    );
                                })}

                            </ul>
                        </div>
                        <div className="border-t bg-white p-2 sm:p-4">

                            <AddCommentInput
                                currentUser={user}
                                lead={showPopup}
                                updateLead={updateLead}
                                refetch={refetch}
                                setShowPopup={setShowPopup}
                            />
                        </div>
                    </div>
                )}
            </div>
            {!createLead && leadsType === "wonleads" && (<div className="mt-8 ">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-6 mb-8">
                    <div className="bg-gradient-to-r from-[#1cb354] to-[#1cb354] text-white
      rounded-lg  sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                        <div className="flex items-center justify-between">
                            <div className="flex gap-2 items-center">
                                <HandCoins className="w-8 h-8 sm:h-12 sm:w-12 opacity-80" />
                                <p className="text-blue-100 text-[16px] sm:text-[20px] lg:text-[25px]">Total Amount</p>
                            </div>
                            <p className="text-[16px] sm:text-2xl lg:text-3xl font-bold">
                                ${totalWonAmount.toLocaleString()}
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-[#f26711] to-[#f26711] text-white
      rounded-lg sm:rounded-2xl p-3 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                        <div className="flex items-center justify-between">
                            <div className="flex gap-2 items-center">
                                <HandCoins className="w-8 h-8 sm:h-12 sm:w-12 opacity-80" />
                                <p className="text-blue-100 text-[16px] sm:text-[20px] lg:text-[25px]">Total Lost</p>
                            </div>
                            <p className="text-[16px] sm:text-2xl lg:text-3xl font-bold">
                                ${totalLostAmount.toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>)}


        </div>
    );
}
